// @tennis-matches/services/winterdoppel/blocks
// Spieltermin-basierte Abwesenheiten + KW-Regel-Ausnahmen (Same-Day-Blöcke)

import {
  generateSessionDates,
  isSessionDateExcludedByWeekRule,
} from "./dateUtils.js";
import { isYmdWithinSchoolHolidays } from "../school-holidays/school-holidays-display.js";

/** reason: Termin trotz KW-Vorfilter verfügbar */
export const WINTERDOPPEL_BLOCK_REASON_WEEK_OVERRIDE = "week_rule_override";

function __asIso(v) {
  return String(v ?? "").trim();
}

function __blockStart(b) {
  return __asIso(b?.start_date ?? b?.startDate);
}

function __blockEnd(b) {
  const s = __blockStart(b);
  const e = __asIso(b?.end_date ?? b?.endDate);
  return e || s;
}

export function isWeekRuleOverrideBlock(b) {
  return String(b?.reason || "") === WINTERDOPPEL_BLOCK_REASON_WEEK_OVERRIDE;
}

/** Session-Termine der Serie (Default-Wochentag: Montag). */
export function sessionDatesForSeries(series) {
  if (!series) return [];
  const weekdays = Array.isArray(series.weekdays)
    ? series.weekdays
    : Array.isArray(series.week_days)
      ? series.week_days
      : [1];
  return generateSessionDates({
    seasonStart: series.season_start || series.seasonStart,
    seasonEnd: series.season_end || series.seasonEnd,
    weekdays: weekdays.length ? weekdays : [1],
  });
}

function __sessionSet(sessionDates) {
  return new Set(
    (Array.isArray(sessionDates) ? sessionDates : [])
      .map(__asIso)
      .filter(Boolean),
  );
}

function __daysCoveredByBlocks(blocks, sessionDates, predicate) {
  const sessions = __sessionSet(sessionDates);
  const days = new Set();
  for (const b of Array.isArray(blocks) ? blocks : []) {
    if (predicate && !predicate(b)) continue;
    const s = __blockStart(b);
    const e = __blockEnd(b);
    if (!s || !e) continue;
    for (const d of sessions) {
      if (d >= s && d <= e) days.add(d);
    }
  }
  return days;
}

function __toSameDayBlocks(isoDates, reason = null) {
  const uniq = [...new Set(isoDates.map(__asIso).filter(Boolean))].sort();
  return uniq.map((d) => ({
    start_date: d,
    end_date: d,
    reason: reason || null,
  }));
}

function __assembleSessionBlocks(absenceDays, overrideDays) {
  const absences = new Set(absenceDays);
  const overrides = new Set(overrideDays);
  for (const d of absences) overrides.delete(d);
  return [
    ...__toSameDayBlocks([...absences], null),
    ...__toSameDayBlocks(
      [...overrides],
      WINTERDOPPEL_BLOCK_REASON_WEEK_OVERRIDE,
    ),
  ].sort((a, b) =>
    a.start_date < b.start_date ? -1 : a.start_date > b.start_date ? 1 : 0,
  );
}

function __partitionSessionDays(blocks, sessionDates) {
  return {
    absences: __daysCoveredByBlocks(
      blocks,
      sessionDates,
      (b) => !isWeekRuleOverrideBlock(b),
    ),
    overrides: __daysCoveredByBlocks(
      blocks,
      sessionDates,
      isWeekRuleOverrideBlock,
    ),
  };
}

export function isDateBlocked(blocks, isoDate) {
  const day = __asIso(isoDate);
  if (!day) return false;
  const list = Array.isArray(blocks) ? blocks : [];
  for (const b of list) {
    if (isWeekRuleOverrideBlock(b)) continue;
    const s = __blockStart(b);
    const e = __blockEnd(b);
    if (s && e && day >= s && day <= e) return true;
  }
  return false;
}

/** true = Termin trotz KW-Regel freigeschaltet. */
export function isWeekRuleOverrideDate(blocks, isoDate) {
  const day = __asIso(isoDate);
  if (!day) return false;
  const list = Array.isArray(blocks) ? blocks : [];
  for (const b of list) {
    if (!isWeekRuleOverrideBlock(b)) continue;
    const s = __blockStart(b);
    const e = __blockEnd(b);
    if (s && e && day >= s && day <= e) return true;
  }
  return false;
}

/** Abgedeckte Spieltermine als sortierte ISO-Liste (nur Abwesenheiten). */
export function blockedSessionDates(blocks, sessionDates) {
  const sessions = Array.isArray(sessionDates) ? sessionDates.map(__asIso) : [];
  return sessions.filter((d) => d && isDateBlocked(blocks, d));
}

/**
 * Same-Day-Ranges als Einzeltage (start===end), Mehr-Tage unverändert.
 * Sortiert nach start_date.
 */
export function normalizeBlocksForDisplay(blocks) {
  const list = Array.isArray(blocks) ? blocks : [];
  return list
    .map((b) => {
      const start = __blockStart(b);
      const end = __blockEnd(b);
      if (!start) return null;
      const sameDay = !end || end === start;
      return {
        ...b,
        start_date: start,
        end_date: sameDay ? start : end,
        reason: b.reason ?? null,
        isSingleDay: sameDay,
      };
    })
    .filter(Boolean)
    .sort((a, b) =>
      a.start_date < b.start_date ? -1 : a.start_date > b.start_date ? 1 : 0,
    );
}

/**
 * Expandiert bestehende Blocks auf Spieltermine als Same-Day-Blöcke.
 * Kalendertage außerhalb der Session-Liste entfallen. Gründe bleiben erhalten.
 */
export function expandBlocksToSessionDays(blocks, sessionDates) {
  const { absences, overrides } = __partitionSessionDays(blocks, sessionDates);
  return __assembleSessionBlocks(absences, overrides);
}

/**
 * Klick-Toggle: Einzeltag ohne Von/Bis. Ignoriert Daten außerhalb sessionDates.
 * Nur Abwesenheiten (keine KW-Ausnahmen).
 */
export function toggleSessionAbsence(blocks, isoDate, sessionDates) {
  const day = __asIso(isoDate);
  const sessions = __sessionSet(sessionDates);
  if (!day || !sessions.has(day)) {
    return expandBlocksToSessionDays(blocks, sessionDates);
  }
  const { absences, overrides } = __partitionSessionDays(blocks, sessionDates);
  if (absences.has(day)) absences.delete(day);
  else {
    absences.add(day);
    overrides.delete(day);
  }
  return __assembleSessionBlocks(absences, overrides);
}

/**
 * Termin-Klick mit KW-Vorfilter:
 * - innerhalb der Regel: Abwesenheit toggeln
 * - außerhalb der Regel: Ausnahme (doch verfügbar) toggeln
 */
export function toggleSessionDateAvailability(
  blocks,
  isoDate,
  sessionDates,
  weekRule = "all",
) {
  const day = __asIso(isoDate);
  const sessions = __sessionSet(sessionDates);
  if (!day || !sessions.has(day)) {
    return expandBlocksToSessionDays(blocks, sessionDates);
  }
  const { absences, overrides } = __partitionSessionDays(blocks, sessionDates);
  if (isSessionDateExcludedByWeekRule(weekRule, day)) {
    if (overrides.has(day)) overrides.delete(day);
    else {
      overrides.add(day);
      absences.delete(day);
    }
  } else if (absences.has(day)) {
    absences.delete(day);
  } else {
    absences.add(day);
    overrides.delete(day);
  }
  return __assembleSessionBlocks(absences, overrides);
}

/**
 * Range blockiert nur Spieltermine in [from, to] ∩ sessionDates.
 * Grenzen außerhalb der Session-Liste werden ignoriert.
 */
export function applySessionRangeBlocks(blocks, from, to, sessionDates) {
  let start = __asIso(from);
  let end = __asIso(to || from);
  if (!start) return expandBlocksToSessionDays(blocks, sessionDates);
  if (!end) end = start;
  if (end < start) {
    const tmp = start;
    start = end;
    end = tmp;
  }
  const sessions = (Array.isArray(sessionDates) ? sessionDates : [])
    .map(__asIso)
    .filter((d) => d && d >= start && d <= end);
  const { absences, overrides } = __partitionSessionDays(blocks, sessionDates);
  for (const d of sessions) {
    absences.add(d);
    overrides.delete(d);
  }
  return __assembleSessionBlocks(absences, overrides);
}

/** Schulferien-Tage, die auf Spieltermine fallen, als Abwesenheit ergänzen. */
export function applySchoolHolidayAbsences(blocks, holidays, sessionDates) {
  const { absences, overrides } = __partitionSessionDays(blocks, sessionDates);
  for (const d of Array.isArray(sessionDates) ? sessionDates : []) {
    const day = __asIso(d);
    if (day && isYmdWithinSchoolHolidays(day, holidays)) {
      absences.add(day);
      overrides.delete(day);
    }
  }
  return __assembleSessionBlocks(absences, overrides);
}

/**
 * Eine Ferienperiode toggeln: fehlende Abwesenheiten setzen bzw. alle
 * betroffenen Spieltermine wieder freigeben.
 */
export function toggleSchoolHolidayAbsences(blocks, holiday, sessionDates) {
  const holidays = holiday ? [holiday] : [];
  const days = (Array.isArray(sessionDates) ? sessionDates : [])
    .map(__asIso)
    .filter((d) => d && isYmdWithinSchoolHolidays(d, holidays));
  if (!days.length) {
    return expandBlocksToSessionDays(blocks, sessionDates);
  }
  const { absences, overrides } = __partitionSessionDays(blocks, sessionDates);
  const allBlocked = days.every((d) => absences.has(d));
  if (allBlocked) {
    for (const d of days) absences.delete(d);
  } else {
    for (const d of days) {
      absences.add(d);
      overrides.delete(d);
    }
  }
  return __assembleSessionBlocks(absences, overrides);
}

export function schoolHolidayAbsenceActive(blocks, holiday, sessionDates) {
  const holidays = holiday ? [holiday] : [];
  const days = (Array.isArray(sessionDates) ? sessionDates : [])
    .map(__asIso)
    .filter((d) => d && isYmdWithinSchoolHolidays(d, holidays));
  if (!days.length) return false;
  const covered = new Set(blockedSessionDates(blocks, sessionDates));
  return days.every((d) => covered.has(d));
}
